
import React, { useState, useRef, useEffect } from 'react';
import './HomePage.css';
import PageHeader from '../../components/shared/PageHeader';
import KpiTile from '../../components/shared/KpiTile';
import Card from '../../components/shared/Card';
import Button from '../../components/shared/Button';
import Badge from '../../components/shared/Badge';
import SilverIcon from '../../components/shared/SilverIcon';
import { useToast } from '../../context/ToastContext';
import { useAppState } from '../../context/AppStateContext';

const KPI_DATA = [
  { label: 'Revenue', value: '$48,291', delta: '12.4%', deltaUp: true, icon: '💰', accent: 'var(--color-success)' },
  { label: 'Active Clients', value: '1,284', delta: '3.2%', deltaUp: true, icon: '👥', accent: 'var(--color-ocean-500)' },
  { label: 'Open Invoices', value: '37', delta: '5.1%', deltaUp: false, icon: '📄', accent: 'var(--color-warning)' },
  { label: 'AI Requests', value: '9,410', delta: '28%', deltaUp: true, icon: '🤖', accent: 'var(--color-purple)' },
];

const SCHEDULE = [
  { time: '09:00', title: 'Stand-up with Team', type: 'call', join: true },
  { time: '10:30', title: 'Client Review — Acme Corp', type: 'meeting', join: false },
  { time: '12:00', title: 'Lunch Break', type: 'personal', join: false },
  { time: '14:00', title: 'Product Demo — Beta', type: 'call', join: true },
  { time: '16:30', title: 'Weekly Analytics Review', type: 'meeting', join: false },
];

const LOG_ENTRIES = [
  { level: 'info', msg: '[sys] Scheduler started. Next job: analytics-sync in 12m' },
  { level: 'info', msg: '[api] GET /api/crm/contacts → 200 (43ms)' },
  { level: 'warn', msg: '[db] Slow query detected: 1.2s on contacts_view' },
  { level: 'info', msg: '[ai] Inference request completed: 312 tokens' },
  { level: 'error', msg: '[webhook] Delivery failed for endpoint /notify (timeout)' },
  { level: 'info', msg: '[billing] Invoice #INV-0047 marked as paid' },
];

const WEATHER = {
  city: 'San Francisco',
  temp: '64°F',
  feel: '61°F',
  humidity: '72%',
  wind: '12 mph',
  condition: 'Partly Cloudy',
  icon: '⛅',
};

const TASKS = [
  { id: 1, text: 'Review Q1 proposal for Acme Corp', done: false, priority: 'high' },
  { id: 2, text: 'Update CRM pipeline stages', done: true, priority: 'medium' },
  { id: 3, text: 'Onboard Sarah M. to dashboard', done: false, priority: 'medium' },
  { id: 4, text: 'Fix billing export bug', done: false, priority: 'high' },
  { id: 5, text: 'Schedule Q2 kickoff meeting', done: true, priority: 'low' },
];

const QUICK_ACTIONS = [
  { icon: 'inbox' as const, label: 'Compose Email', page: 'business' as const, tab: 'inbox' as const },
  { icon: 'customers' as const, label: 'Add Customer', page: 'business' as const, tab: 'customers' as const },
  { icon: 'money' as const, label: 'Create Invoice', page: 'business' as const, tab: 'money' as const },
  { icon: 'ai' as const, label: 'Open AI Hub', page: 'studio' as const, tab: 'ai' as const },
  { icon: 'analytics' as const, label: 'Analytics', page: 'business' as const, tab: 'analytics' as const },
  { icon: 'database' as const, label: 'Database', page: 'ops' as const, tab: 'database' as const },
];

export default function HomePage() {
  const { addToast } = useToast();
  const { setAppState } = useAppState();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [logPaused, setLogPaused] = useState(false);
  const [terminalExpanded, setTerminalExpanded] = useState(false);
  const [logs, setLogs] = useState(LOG_ENTRIES);
  const [tasks, setTasks] = useState(TASKS);
  const lastPos = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (logPaused) return;
    const interval = setInterval(() => {
      const sample = LOG_ENTRIES[Math.floor(Math.random() * LOG_ENTRIES.length)];
      setLogs(l => [...l.slice(-30), { ...sample, msg: `[${new Date().toLocaleTimeString()}] ${sample.msg.replace(/\[.*?\]\s/, '')}` }]);
    }, 3000);
    return () => clearInterval(interval);
  }, [logPaused]);

  const getPos = (e: React.MouseEvent | React.TouchEvent, canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect();
    const src = 'touches' in e ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const startDraw = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current; if (!canvas) return;
    setIsDrawing(true);
    lastPos.current = getPos(e, canvas);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d'); if (!ctx) return;
    const pos = getPos(e, canvas);
    ctx.strokeStyle = '#1282D6';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    if (lastPos.current) { ctx.moveTo(lastPos.current.x, lastPos.current.y); }
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
  };

  const stopDraw = () => { setIsDrawing(false); lastPos.current = null; };
  const clearCanvas = () => {
    const canvas = canvasRef.current; if (!canvas) return;
    canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
  };
  const toggleTask = (id: number) => setTasks(ts => ts.map(t => t.id === id ? { ...t, done: !t.done } : t));

  const navTo = (page: 'business' | 'studio' | 'ops', tab?: any) => {
    setAppState(s => ({
      ...s,
      currentPage: page,
      ...(page === 'business' && tab ? { businessTab: tab } : {}),
      ...(page === 'studio' && tab ? { studioTab: tab } : {}),
      ...(page === 'ops' && tab ? { opsTab: tab } : {}),
    }));
  };

  return (
    <div className="home-page">
      <PageHeader
        title="Command Center"
        breadcrumb={['Home']}
        actions={
          <>
            <Button variant="ghost" size="sm" className="home-header-btn-hide-mobile">Edit Widgets</Button>
            <Button size="sm" icon="+" onClick={() => addToast('info', 'Widget picker coming soon!')}>Add Widget</Button>
          </>
        }
      />

      {/* KPI Tiles */}
      <div className="home-kpi-grid">
        {KPI_DATA.map(k => <KpiTile key={k.label} {...k} />)}
      </div>

      {/* Row 2: Schedule + Weather */}
      <div className="home-row2">
        <Card
          title="Today's Schedule"
          className="home-schedule-card"
          actions={<button className="card-icon-btn" onClick={() => navTo('business', 'calendar')}>+</button>}
        >
          <div className="schedule-list">
            {SCHEDULE.map((s, i) => (
              <div key={i} className={`schedule-item schedule-item--${s.type}`}>
                <div className="schedule-time">{s.time}</div>
                <div className="schedule-dot" />
                <div className="schedule-info">
                  <div className="schedule-title">{s.title}</div>
                  <Badge variant={s.type === 'call' ? 'ocean' : s.type === 'personal' ? 'purple' : 'default'}>{s.type}</Badge>
                </div>
                {s.join && (
                  <Button size="sm" onClick={() => addToast('info', 'Starting call…')}>
                    Join
                  </Button>
                )}
              </div>
            ))}
          </div>
        </Card>

        <Card title="Weather" actions={<Badge variant="ocean">Live</Badge>} className="home-weather-card">
          <div className="weather-main">
            <div className="weather-icon">{WEATHER.icon}</div>
            <div>
              <div className="weather-temp">{WEATHER.temp}</div>
              <div className="weather-cond">{WEATHER.condition}</div>
              <div className="weather-loc">📍 {WEATHER.city}</div>
            </div>
          </div>
          <div className="weather-stats">
            <div className="weather-stat"><span>Feels Like</span><strong>{WEATHER.feel}</strong></div>
            <div className="weather-stat"><span>Humidity</span><strong>{WEATHER.humidity}</strong></div>
            <div className="weather-stat"><span>Wind</span><strong>{WEATHER.wind}</strong></div>
          </div>
        </Card>
      </div>

      {/* Row 3: Quick Actions + Terminal + Signature */}
      <div className="home-row3">
        <Card title="Quick Actions" className="home-actions-card">
          <div className="quick-actions">
            {QUICK_ACTIONS.map(a => (
              <button
                key={a.label}
                className="quick-action-btn"
                onClick={() => navTo(a.page, a.tab)}
              >
                <SilverIcon name={a.icon} size={16} style={{ color: 'var(--color-ocean-500)', flexShrink: 0 }} />
                <span>{a.label}</span>
              </button>
            ))}
          </div>
        </Card>

        <Card
          title="System Terminal"
          className="home-terminal-card"
          actions={
            <>
              <button className="card-icon-btn" onClick={() => setTerminalExpanded(e => !e)} title="Expand">{terminalExpanded ? '⊡' : '⊞'}</button>
              <button className="card-icon-btn" onClick={() => setLogPaused(p => !p)} title={logPaused ? 'Resume' : 'Pause'}>{logPaused ? '▶' : '⏸'}</button>
              <button className="card-icon-btn" onClick={() => setLogs([])} title="Clear">✕</button>
            </>
          }
        >
          <div className={`terminal${terminalExpanded ? ' terminal--expanded' : ''}`}>
            {logs.map((entry, i) => (
              <div key={i} className={`terminal__line terminal__line--${entry.level}`}>
                <span className="terminal__level">[{entry.level.toUpperCase()}]</span>
                <span className="terminal__msg">{entry.msg}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card
          title="Digital Signature"
          className="home-sig-card"
          actions={<button className="card-icon-btn" onClick={clearCanvas}>Clear</button>}
          footer={
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <Button variant="ghost" size="sm" onClick={clearCanvas}>Undo</Button>
              <Button size="sm" onClick={() => addToast('success', 'Signature saved!')}>Save</Button>
            </div>
          }
        >
          <div className="sig-canvas-wrap">
            <canvas
              ref={canvasRef}
              className="sig-canvas"
              width={300}
              height={140}
              onMouseDown={startDraw}
              onMouseMove={draw}
              onMouseUp={stopDraw}
              onMouseLeave={stopDraw}
              onTouchStart={startDraw}
              onTouchMove={draw}
              onTouchEnd={stopDraw}
            />
            <div className="sig-hint">Sign above</div>
          </div>
        </Card>
      </div>

      {/* Row 4: Activity + Tasks */}
      <div className="home-row4">
        <Card title="Recent Activity" actions={<Button variant="ghost" size="sm" onClick={() => navTo('business', 'analytics')}>View All</Button>} className="home-activity-card">
          <div className="activity-feed">
            {[
              { icon: '💳', text: 'Invoice #INV-0047 paid by Acme Corp', time: '2m ago', type: 'success' },
              { icon: '👤', text: 'New contact added: Sarah Mitchell', time: '15m ago', type: 'info' },
              { icon: '🤖', text: 'AI Hub: GPT-4o session — 1,240 tokens', time: '32m ago', type: 'purple' },
              { icon: '⚠', text: 'Slow DB query on contacts_view', time: '1h ago', type: 'warning' },
              { icon: '📅', text: 'Product Demo scheduled for 14:00', time: '2h ago', type: 'info' },
            ].map((a, i) => (
              <div key={i} className="activity-item">
                <div className={`activity-icon activity-icon--${a.type}`}>{a.icon}</div>
                <div className="activity-content">
                  <span className="activity-text">{a.text}</span>
                  <span className="activity-time">{a.time}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card
          title="Tasks"
          className="home-tasks-card"
          actions={<button className="card-icon-btn" onClick={() => addToast('info', 'Add task')}>+</button>}
        >
          <div className="tasks-list">
            {tasks.map(task => (
              <div key={task.id} className={`task-item${task.done ? ' task-item--done' : ''}`} onClick={() => toggleTask(task.id)}>
                <div className={`task-check${task.done ? ' task-check--done' : ''}`}>{task.done && '✓'}</div>
                <span className="task-text">{task.text}</span>
                <span className={`task-priority task-priority--${task.priority}`} />
              </div>
            ))}
          </div>
          <div className="tasks-footer">
            <span className="tasks-count">{tasks.filter(t => t.done).length}/{tasks.length} complete</span>
            <div className="tasks-progress-bg">
              <div className="tasks-progress-fill" style={{ width: `${(tasks.filter(t => t.done).length / tasks.length) * 100}%` }} />
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
